import React from "react";
import { Box, Typography, CircularProgress, Tabs, Tab, Chip } from "@material-ui/core";
import { css, cx } from "@emotion/css";
import { LOCALES } from "../api";
import type { PreviewAttrs, PreviewLocale } from "../api";
import { useLocaleDiffPreviews, formatLocaleLabel } from "../useLocaleDiffPreviews";
import type { SnapshotRef } from "../useLocaleDiffPreviews";
import QuestionnaireDiff from "./QuestionnaireDiff";

const DiffShell: React.VFC<{
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  before: SnapshotRef | null;
  after: SnapshotRef | null;
  draftId: number | null;
  attrs: PreviewAttrs;
  onAttrsChange: React.Dispatch<React.SetStateAction<PreviewAttrs>>;
  previousLabel: string;
  nextLabel: string;
  emptyMessage: React.ReactNode;
  actions?: React.ReactNode;
  isLoading?: boolean;
  error?: React.ReactNode;
  embedded?: boolean;
}> = ({
  title,
  subtitle,
  before,
  after,
  draftId,
  attrs,
  onAttrsChange,
  previousLabel,
  nextLabel,
  emptyMessage,
  actions,
  isLoading,
  error,
  embedded,
}) => {
  const { localeInfo, current, previewsLoading, anyError } = useLocaleDiffPreviews({
    before,
    after,
    draftId,
    attrs,
  });

  const loading = isLoading || previewsLoading;
  const showError = !loading && (error || anyError);
  const showEmpty = !loading && !showError && (!before || !after);
  const showDiff = !loading && !showError && !showEmpty && current.before && current.after;

  const allLocalesLoaded = localeInfo.every(l => l.count !== null);
  const totalCount = localeInfo.reduce((sum, l) => sum + (l.count ?? 0), 0);

  return (
    <Box className={cx(styles.container, embedded && styles.embedded)}>
      <Box className={styles.header}>
        <Box>
          <Box className={styles.title}>
            <Typography variant="h5">{title}</Typography>
            {showDiff && allLocalesLoaded && (
              <Chip
                size="small"
                label={`${totalCount} change${totalCount === 1 ? "" : "s"}`}
                color={totalCount === 0 ? "default" : "primary"}
              />
            )}
          </Box>
          {subtitle}
        </Box>
        <Box className={styles.actions}>
          {actions}
          <Tabs
            value={attrs.locale}
            onChange={(_, locale) => onAttrsChange(a => ({ ...a, locale: locale as PreviewLocale }))}
            indicatorColor="primary"
            textColor="primary"
          >
            {LOCALES.map(l => (
              <Tab
                key={l}
                value={l}
                label={formatLocaleLabel(
                  l,
                  localeInfo.find(i => i.locale === l)
                )}
              />
            ))}
          </Tabs>
        </Box>
      </Box>

      <Box className={styles.body}>
        {loading ? (
          <Box className={styles.centered}>
            <CircularProgress />
          </Box>
        ) : showError ? (
          <Typography color="error" className={styles.message}>
            {error ?? "Failed to load preview data"}
          </Typography>
        ) : showEmpty ? (
          <Typography variant="body2" color="textSecondary" className={styles.message}>
            {emptyMessage}
          </Typography>
        ) : showDiff ? (
          <QuestionnaireDiff
            previous={current.before!.questionnaire}
            next={current.after!.questionnaire}
            previousLabel={previousLabel}
            nextLabel={nextLabel}
          />
        ) : null}
      </Box>
    </Box>
  );
};

export default DiffShell;

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    height: calc(100vh - 90px);
    padding: 16px;
    box-sizing: border-box;
    gap: 16px;
  `,
  embedded: css`
    height: 100%;
  `,
  header: css`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;
  `,
  title: css`
    display: flex;
    align-items: center;
    gap: 8px;
  `,
  actions: css`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    flex-wrap: wrap;
  `,
  body: css`
    flex: 1;
    overflow: auto;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    background: #fff;
  `,
  centered: css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
  `,
  message: css`
    padding: 16px;
  `,
};
